// Gift.jsx
import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import DecryptionWrapper from '../container/DecryptionWrapper.js';

import StatefulImage from './StatefulImage.jsx';
import LazyImage, { Image } from './LazyImage.jsx';
import { textColor } from '../style/variables.js';

import env from '../resource/env.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import { MediaAssetFormat } from '../resource/getMediaAsset.js';
import { ButtonId } from '../resource/mixpanel.js';
import getAPNG from '../resource/getAPNG.js';
import getPag, { isPagCached } from '../resource/getPag.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import { livestream as livestreamDebug } from '../resource/debug.js';
import { startSentryLogCapture } from '../resource/sentry.js';
import { LOG_CAPTURE_LIVESTREAM_SEND_GIFT } from '../resource/sentryLogCaptureNames.js';

import ResetButtonStyle from '../style/ResetButtonStyle.js';

import LoadingImage from '../../img/placeholder-gift.svg';
import LoadingImageDark from '../../img/img-loading-dark-20.svg';
import DownloadIcon from '../../img/icon-download-green.svg';
import WarningIcon from '../../img/icon-warning-gray.svg';
import GiftPlaceholder from '../../img/img_gift_placeholder.svg';
import DiamondImage from '../../img/point_2.svg';

const log = livestreamDebug.extend('log:Gift');
const errorLog = livestreamDebug.extend('error:Gift');

const giftAllowTypes = ['panel', 'livestream'];
const giftButtonIdList = {
  panel: ButtonId.All.ButtonGiftChatroom,
  livestream: ButtonId.All.ButtonGiftLiveStream,
};

export class Gift extends React.PureComponent {
  state = {
    isHovered: false,
    isLoading: false,
    isLoadSucceed: null,
    hasParsed: null,
  };
  isPagDownloading = false;
  canvasRef = createRef();
  apngPlayer = null;
  componentDidMount() {
    this.checkPagCache();
    this.checkAPNG();
  }
  componentDidUpdate(prevProps) {
    const { isActive } = this.props;
    if (isActive && isActive !== prevProps.isActive) {
      this.checkAPNG();
    }
  }
  componentWillUnmount() {
    if (this.apngPlayer) {
      this.apngPlayer.stop();
      this.apngPlayer = null;
    }
  }
  checkPagCache() {
    const { pagImage, isPagGift } = this.props;
    if (!isPagGift || !pagImage) {
      return;
    }

    // Check if PAG is already in cache (no network request)
    const isCached = isPagCached(pagImage);
    this.setState({ hasParsed: isCached ? true : false });
  }
  handleOnMouseEnter = () => {
    this.setState({ isHovered: true });
  };
  handleOnMouseLeave = () => {
    this.setState({ isHovered: false });
  };
  handleOnClick = async () => {
    const {
      id,
      type,
      isSwiping,
      notifySendGift,
      sendGift,
      isAnimationGift,
      isPagGift,
      hasEnoughBalance,
      addvalue,
      isLoggedIn,
      login,
      openGiftSendConfirmModal,
      isNeedSendGiftConfirm,
    } = this.props;

    if (isSwiping) {
      return;
    }

    if (!isLoggedIn) {
      login();
      return;
    }

    if (!hasEnoughBalance) {
      addvalue();
      return;
    }

    startSentryLogCapture({ captureName: LOG_CAPTURE_LIVESTREAM_SEND_GIFT });
    log(`gift [${id}] clicked by user`, { giftId: id, isPagGift });

    // pag gift click to download
    if (isPagGift && !this.state.hasParsed) {
      if (this.isPagDownloading) {
        return;
      }

      log(`gift [${id}] PAG download`, { giftId: id });
      this.isPagDownloading = true;
      const isPagSuccess = await this.checkPagGift();
      this.isPagDownloading = false;

      if (!isPagSuccess) return;
    }

    // Only livestream gift need send gift confirm modal now.
    if ('livestream' === type && isNeedSendGiftConfirm) {
      log(`gift [${id}] opening send confirm modal`, { giftId: id });
      openGiftSendConfirmModal({ giftId: id });
      return;
    }

    if (isAnimationGift || isPagGift) {
      notifySendGift({ giftId: id });
    } else {
      sendGift({ giftId: id });
    }
  };

  async checkPagGift() {
    const { pagImage, id, openAnimationFailedModal } = this.props;

    if (!pagImage) {
      errorLog(`gift [${id}] PAG download failed: no pagImage`, { giftId: id });
      return false;
    }

    this.setState({ isLoading: true });
    const { isPAGSupported, pag } = await getPag({
      imageSource: pagImage,
    });
    if (isPAGSupported && pag) {
      log(`gift [${id}] PAG download success`, { giftId: id });
      this.setState({
        isLoading: false,
        isLoadSucceed: true,
        hasParsed: true,
      });
      return true;
    } else {
      errorLog(`gift [${id}] PAG download failed`, {
        giftId: id,
        isPAGSupported,
        hasPag: !!pag,
      });
      this.setState({
        isLoading: false,
        isLoadSucceed: false,
        hasParsed: false,
      });
      openAnimationFailedModal({
        giftId: id,
        isAnimationGift: false,
        isPagGift: true,
      });
      return false;
    }
  }

  async checkAPNG() {
    const { image, isAnimationGift, isActive, id, openAnimationFailedModal } =
      this.props;

    if (!isAnimationGift || !isActive) return;

    if (this.apngPlayer) {
      this.apngPlayer.stop();
      this.apngPlayer = null;
    }

    this.setState({ isLoading: true });

    const context = this.canvasRef.current.getContext('2d');
    const loadingImage = new Image();
    loadingImage.src = LoadingImage;
    loadingImage.onload = () => {
      context.drawImage(loadingImage, 0, 0);
    };

    const { isAPNG, apng } = await getAPNG({ imageSource: image });
    if (!isAPNG || !this.canvasRef.current) {
      log(`checkAPNG failed: ${id}`, {
        giftId: id,
        isAPNG,
        hasCanvas: !!this.canvasRef.current,
      });
      this.setState({
        isLoading: false,
        isLoadSucceed: false,
        hasParsed: false,
      });
      openAnimationFailedModal({
        giftId: id,
        isAnimationGift: true,
        isPagGift: false,
      });
      return;
    }

    this.setState({ hasParsed: true });

    this.canvasRef.current.width = apng.width;
    this.canvasRef.current.height = apng.height;

    const player = await apng.getPlayer(
      this.canvasRef.current.getContext('2d')
    );
    this.apngPlayer = player;
    player.play();

    this.setState({ isLoading: false, isLoadSucceed: true });
  }

  renderPngImage() {
    const { image } = this.props;
    const isPublicImage = image.includes(env.PUBLIC_URL_PREFIX);

    if (isPublicImage) {
      const [avifSrcSet, webpSrcSet, pngSrcSet] = [
        MediaAssetFormat.AVIF,
        MediaAssetFormat.WEBP,
        MediaAssetFormat.PNG,
      ].map(format => getPublicSrcSet({ href: image, size: 128, format }));

      return (
        <LazyImage data-key="target" threshold={0.25}>
          <DecryptionWrapper resourceUrl={pngSrcSet.src}>
            <source srcSet={avifSrcSet.srcSet} type="image/avif" />
            <source srcSet={webpSrcSet.srcSet} type="image/webp" />
            <Image {...pngSrcSet} />
          </DecryptionWrapper>
        </LazyImage>
      );
    } else {
      return (
        <LazyImage data-key="target" threshold={0.25}>
          <Image src={image} />
        </LazyImage>
      );
    }
  }

  renderImage() {
    const { image, isAnimationGift, isPagGift } = this.props;
    const { isHovered, isLoading, isLoadSucceed, hasParsed } = this.state;

    let giftStyle = {
      transition: '0.4s transform ease, 0.4s opacity ease',
      transform: isHovered ? 'scale(0.8)' : 'scale(1)',
      opacity: isHovered ? 0.6 : 1,
    };

    if (isPagGift) {
      return (
        <PagGiftContainer $isHovered={isHovered}>
          <PngImageLayer $isHovered={isHovered} $isLoading={isLoading}>
            <StatefulImage>
              <StatefulImageWrapper data-key="loading">
                <Loader src={LoadingImage} />
              </StatefulImageWrapper>
              <StatefulImageWrapper data-key="error">
                <img src={GiftPlaceholder} />
              </StatefulImageWrapper>
              {this.renderPngImage()}
            </StatefulImage>
          </PngImageLayer>
          {isLoading && (
            <CenterIconOverlay>
              <SpinningLoading src={LoadingImageDark} />
            </CenterIconOverlay>
          )}
          {!isLoading && hasParsed === false && isLoadSucceed === null && (
            <TopRightIconOverlay>
              <DownloadIconImage src={DownloadIcon} />
            </TopRightIconOverlay>
          )}
          {!isLoading && isLoadSucceed === false && (
            <TopRightIconOverlay>
              <WarningIconImage src={WarningIcon} />
            </TopRightIconOverlay>
          )}
        </PagGiftContainer>
      );
    } else if (isAnimationGift) {
      return (
        <ApngGiftContainer $isHovered={isHovered}>
          <CanvasLayer $isLoading={isLoading}>
            <AnimationGift
              style={giftStyle}
              width={72}
              height={72}
              ref={this.canvasRef}
            />
          </CanvasLayer>
          {isLoading && (
            <CenterIconOverlay>
              <SpinningLoading src={LoadingImageDark} />
            </CenterIconOverlay>
          )}
          {!isLoading && isLoadSucceed === false && (
            <TopRightIconOverlay>
              <WarningIconImage src={WarningIcon} />
            </TopRightIconOverlay>
          )}
        </ApngGiftContainer>
      );
    }

    const isPublicImage = image.includes(env.PUBLIC_URL_PREFIX);
    let targetImageElement = null;
    if (isPublicImage) {
      const [avifSrcSet, webpSrcSet, pngSrcSet] = [
        MediaAssetFormat.AVIF,
        MediaAssetFormat.WEBP,
        MediaAssetFormat.PNG, // should use PNG for transparent background
      ].map(format => getPublicSrcSet({ href: image, size: 128, format }));
      targetImageElement = (
        <LazyImage data-key="target" threshold={0.25}>
          <DecryptionWrapper resourceUrl={pngSrcSet.src}>
            <source srcSet={avifSrcSet.srcSet} type="image/avif" />
            <source srcSet={webpSrcSet.srcSet} type="image/webp" />
            <Image {...pngSrcSet} style={giftStyle} />
          </DecryptionWrapper>
        </LazyImage>
      );
    } else {
      targetImageElement = (
        <LazyImage data-key="target" threshold={0.25}>
          {/* DecryptionWrapper is not required since it's not public */}
          <Image src={image} style={giftStyle} />
        </LazyImage>
      );
    }

    return (
      <StatefulImage>
        <StatefulImageWrapper data-key="loading">
          <Loader src={LoadingImage} />
        </StatefulImageWrapper>
        <StatefulImageWrapper data-key="error">
          <img src={GiftPlaceholder} />
        </StatefulImageWrapper>
        {targetImageElement}
      </StatefulImage>
    );
  }

  renderPanelGift() {
    const { i18nKey, price, t, type } = this.props;
    return (
      <>
        <ImageWrapper onDragStart={e => e.preventDefault()}>
          {this.renderImage()}
        </ImageWrapper>
        <Title>
          {t(i18nKey, {
            ns: TranslationNamespace.GIFT,
          })}
        </Title>
        <Price type={type}>
          <Diamond type={type} src={DiamondImage} />
          {price}
        </Price>
      </>
    );
  }

  render() {
    const { id, streamId, type, isSendingGift } = this.props;
    const { isLoading } = this.state;

    return (
      <StyledGift
        data-element_id={giftButtonIdList[type]}
        data-click_data__product_id={id}
        data-tracking_payload={{
          'product.id': id,
          'stream.id': streamId,
        }}
        disabled={isSendingGift || isLoading}
        onClick={this.handleOnClick}
        onMouseEnter={this.handleOnMouseEnter}
        onMouseLeave={this.handleOnMouseLeave}
        onTouchStart={this.handleOnMouseEnter}
        onTouchMove={this.handleOnMouseLeave}
        onTouchEnd={this.handleOnMouseLeave}
        onMouseDown={this.handleOnMouseEnter}
        onMouseUp={this.handleOnMouseLeave}
      >
        {this.renderPanelGift()}
      </StyledGift>
    );
  }
}

Gift.propTypes = {
  id: PropTypes.string,
  streamId: PropTypes.string,
  i18nKey: PropTypes.string,
  type: PropTypes.oneOf(giftAllowTypes),
  image: PropTypes.string,
  pagImage: PropTypes.string,
  price: PropTypes.number,
  isSwiping: PropTypes.bool,
  isAnimationGift: PropTypes.bool,
  isPagGift: PropTypes.bool,
  isActive: PropTypes.bool,
  isSendingGift: PropTypes.bool,
  hasEnoughBalance: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  t: PropTypes.func.isRequired,
  sendGift: PropTypes.func,
  notifySendGift: PropTypes.func,
  pushToastr: PropTypes.func,
  addvalue: PropTypes.func,
  login: PropTypes.func,
  openAnimationFailedModal: PropTypes.func,
  openGiftSendConfirmModal: PropTypes.func,
  isNeedSendGiftConfirm: PropTypes.bool,
};

Gift.defaultProps = {
  id: '',
  streamId: '',
  type: giftAllowTypes[0],
  i18nKey: '',
  image: '',
  pagImage: '',
  price: 0,
  isAnimationGift: false,
  isPagGift: false,
  isActive: false,
  isSendingGift: false,
  hasEnoughBalance: false,
  isLoggedIn: false,
  sendGift: NULL_FUNCTION,
  notifySendGift: NULL_FUNCTION,
  pushToastr: NULL_FUNCTION,
  addvalue: NULL_FUNCTION,
  login: NULL_FUNCTION,
  openAnimationFailedModal: NULL_FUNCTION,
  openGiftSendConfirmModal: NULL_FUNCTION,
  isNeedSendGiftConfirm: true,
};

export const StyledGift = styled.button`
  ${ResetButtonStyle};
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 12px;
`;

export const ImageWrapper = styled.div`
  width: 72px;
  height: 72px;
`;

const StatefulImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  text-align: center;

  & > img {
    height: 100%;
    width: 100%;
  }
`;

const Loader = styled.img``;

const Title = styled.div`
  margin: 5px 0;
  color: #828282;
`;

const Price = styled.div`
  display: flex;
  align-items: center;
  color: ${({ type }) =>
    type === giftAllowTypes[0] ? textColor.white : textColor.primary};
`;

const Diamond = styled.img`
  vertical-align: top;
  height: ${({ type }) => (type === giftAllowTypes[0] ? '15px' : '12px')};
  margin-right: 0.3em;
`;

const ApngGiftContainer = styled.div`
  width: 72px;
  height: 72px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CanvasLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;

  transition: opacity 0.4s ease-in;

  opacity: ${({ $isLoading }) => {
    if ($isLoading) return 0.3;
    return 1;
  }};

  pointer-events: ${({ $isLoading }) => {
    return $isLoading ? 'none' : 'auto';
  }};
`;

const AnimationGift = styled.canvas`
  width: 72px;
  height: 72px;
`;

const PagGiftContainer = styled.div`
  width: 72px;
  height: 72px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $isHovered }) =>
    $isHovered
      ? `
    animation: breathe 1.0s ease-in-out infinite;
  `
      : ''}

  @keyframes breathe {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }
`;

const PngImageLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;

  transition: opacity 0.4s ease-in;

  opacity: ${({ $isLoading }) => {
    if ($isLoading) return 0.3;
    return 1;
  }};

  pointer-events: ${({ $isLoading }) => {
    return $isLoading ? 'none' : 'auto';
  }};
`;

const CenterIconOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
`;

const TopRightIconOverlay = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SpinningLoading = styled.img`
  width: 100%;
  height: 100%;
`;

const DownloadIconImage = styled.img`
  width: 100%;
  height: 100%;
`;

const WarningIconImage = styled.img`
  width: 100%;
  height: 100%;
`;

export default withTranslation()(Gift);
